import { useEffect, useMemo, useState } from "react";
import { MessageCircle, Trash2, Home as HomeIcon, Building2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const STATUSES = ["New", "Confirmed", "Completed", "Cancelled"];
const FILTERS = ["All", ...STATUSES];

const waLink = (b) => {
  const phone = b.phone.replace(/\D/g, "");
  const intl = phone.length === 10 ? `91${phone}` : phone;
  const text = encodeURIComponent(`Hi ${b.name}, this is Monika from Golden Beauty Touch. Regarding your booking for ${b.service} on ${b.date} at ${b.time} (${b.visit_type}) — `);
  return `https://wa.me/${intl}?text=${text}`;
};

export function BookingsManager() {
  const [bookings, setBookings] = useState(null);
  const [filter, setFilter] = useState("All");

  useEffect(() => {
    api.get("/admin/bookings").then(({ data }) => setBookings(data)).catch((e) => { toast.error(formatApiError(e.response?.data?.detail)); setBookings([]); });
  }, []);

  const counts = useMemo(() => (bookings || []).reduce((acc, b) => ({ ...acc, [b.status]: (acc[b.status] || 0) + 1 }), {}), [bookings]);
  const visible = (bookings || []).filter((b) => filter === "All" || b.status === filter);

  const setStatus = async (id, status) => {
    try {
      const { data } = await api.patch(`/admin/bookings/${id}`, { status });
      setBookings((list) => list.map((b) => (b.id === id ? data : b)));
      toast.success(`Marked as ${status}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this booking request permanently?")) return;
    try {
      await api.delete(`/admin/bookings/${id}`);
      setBookings((list) => list.filter((b) => b.id !== id));
      toast.success("Booking deleted");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <section data-testid="bookings-manager">
      <header className="admin-head">
        <div><p className="eyebrow light">APPOINTMENT REQUESTS</p><h1>Bookings</h1></div>
        <div className="stat-row">{STATUSES.map((s) => <div className={`stat stat-${s.toLowerCase()}`} key={s} data-testid={`stat-${s.toLowerCase()}`}><strong>{counts[s] || 0}</strong><span>{s}</span></div>)}</div>
      </header>
      <div className="filter-row" data-testid="booking-filters">{FILTERS.map((f) => <button key={f} className={filter === f ? "active" : ""} onClick={() => setFilter(f)} data-testid={`filter-${f.toLowerCase()}`}>{f}</button>)}</div>
      {bookings === null ? <p className="muted">Loading bookings…</p> : visible.length === 0 ? <p className="muted empty" data-testid="bookings-empty">No {filter === "All" ? "" : filter.toLowerCase()} bookings yet.</p> : (
        <div className="booking-list">{visible.map((b) => <BookingRow key={b.id} b={b} onStatus={setStatus} onDelete={remove} />)}</div>
      )}
    </section>
  );
}

function BookingRow({ b, onStatus, onDelete }) {
  const created = new Date(b.created_at).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
  return (
    <article className={`booking-row status-${b.status.toLowerCase()}`} data-testid={`booking-row-${b.id}`}>
      <div className="booking-who"><strong data-testid="booking-customer-name">{b.name}</strong><a href={`tel:${b.phone}`}>{b.phone}</a><small>Requested {created}</small></div>
      <div className="booking-what"><strong>{b.service}</strong><span>{b.date} · {b.time}</span><span className="visit">{b.visit_type === "Home Service" ? <HomeIcon size={13} /> : <Building2 size={13} />} {b.visit_type}</span>{b.message && <p>“{b.message}”</p>}</div>
      <div className="booking-actions">
        <select value={b.status} onChange={(e) => onStatus(b.id, e.target.value)} className={`status-select s-${b.status.toLowerCase()}`} data-testid="booking-status-select">{STATUSES.map((s) => <option key={s}>{s}</option>)}</select>
        <a href={waLink(b)} target="_blank" rel="noreferrer" className="wa-reply" data-testid="booking-whatsapp-reply"><MessageCircle size={15} /> Reply</a>
        <button onClick={() => onDelete(b.id)} className="danger" aria-label="Delete booking" data-testid="booking-delete-button"><Trash2 size={15} /></button>
      </div>
    </article>
  );
}
